import {
  connectorConnectionAssetsApi,
  type ConnectorAssetResponsePayload,
} from "../api";

/**
 * Flattened leaf row sourced from `GET /connections/{id}/assets?view=tree`.
 * Reused by the connected-dataset table (selected only) and the edit-dataset
 * modal (all available, includes `isSelected`).
 */
export interface ConnectorTableRow {
  assetId: string;
  /** Leaf name (relational table name, HubSpot object name). */
  table: string;
  /** Schema name for relational shapes; "" for HubSpot/CRM. */
  schema: string;
  /** `null` when this leaf has never had a real count computed (e.g. a
   *  Google Sheets tab that isn't currently selected) — render "-", not a
   *  fabricated number (Google's grid-capacity placeholder used to leak
   *  through here as a misleading "1,000 rows"). */
  rowCount: number | null;
  columnCount: number | null;
  isSelected: boolean;
}

const RELATIONAL_LEAF_TYPE = "relational_table";
const SAAS_LEAF_TYPE = "saas_object";
/** Google Sheets (FILE shape): roots are files, leaves are sheets one
 *  level down — structurally identical to the relational schema→table
 *  shape, just different asset_type strings. */
const FILE_LEAF_TYPE = "sheet";

/**
 * Per the backend's tree contract:
 *   * Snowflake / BigQuery / Postgres (RELATIONAL): roots are schemas,
 *     leaves are tables one level down → max_depth=1.
 *   * HubSpot (OBJECT_SAAS, flat): leaves are themselves the top-level
 *     items → max_depth=0.
 *
 * Connectors not listed default to max_depth=1; safe for any future
 * relational shape.
 */
const TREE_MAX_DEPTH_BY_CATALOG_KEY: Record<string, number> = {
  hubspot: 0,
};

const resolveMaxDepth = (catalogKey: string): number =>
  TREE_MAX_DEPTH_BY_CATALOG_KEY[catalogKey.toLowerCase()] ?? 1;

const toFiniteNumber = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
};

/**
 * Reads a count from the top-level field first (canonical), then falls
 * back to the same key inside `attributes_json` (some shapes echo both;
 * older payloads only carried the inner copy). Returns `null` — not `0` —
 * when neither is present: for Google Sheets specifically, an unselected
 * sheet has genuinely never had a real count fetched, and defaulting to `0`
 * would render as false as the grid-capacity number it replaced.
 */
const readCount = (
  asset: ConnectorAssetResponsePayload,
  field: "row_count" | "column_count",
): number | null => {
  const topLevel = toFiniteNumber(asset[field]);
  if (topLevel !== undefined) return topLevel;
  const fromAttrs = toFiniteNumber(asset.attributes_json?.[field]);
  return fromAttrs ?? null;
};

const toLeafRow = (
  asset: ConnectorAssetResponsePayload,
  schema: string,
): ConnectorTableRow => ({
  assetId: String(asset.id),
  table: asset.name,
  schema,
  rowCount: readCount(asset, "row_count"),
  columnCount: readCount(asset, "column_count"),
  isSelected: asset.is_selected,
});

/**
 * Pure mapper from the tree response to a flat list of leaf table rows.
 * Exported so the same projection can be reused (tests, future surfaces).
 */
export const flattenAssetTreeToTables = (
  items: ConnectorAssetResponsePayload[],
): ConnectorTableRow[] => {
  const rows: ConnectorTableRow[] = [];

  for (const root of items) {
    if (root.asset_type === RELATIONAL_LEAF_TYPE) {
      // Defensive: some relational shapes might land at the root too.
      rows.push(toLeafRow(root, ""));
      continue;
    }

    if (root.asset_type === SAAS_LEAF_TYPE) {
      // HubSpot / CRM: roots are themselves leaves; no schema concept.
      rows.push(toLeafRow(root, ""));
      continue;
    }

    // Relational root (relational_schema) or Google Sheets file root
    // (FILE shape) — both group leaf children one level down.
    const schemaName = root.name;
    for (const child of root.children ?? []) {
      if (
        child.asset_type !== RELATIONAL_LEAF_TYPE &&
        child.asset_type !== FILE_LEAF_TYPE
      ) {
        continue;
      }
      rows.push(toLeafRow(child, schemaName));
    }
  }

  rows.sort((left, right) => left.table.localeCompare(right.table));
  return rows;
};

/**
 * Single-call tree fetch. `view=tree` is unpaginated server-side, so one
 * request returns the full set. Pass `isSelected=true` for the connected
 * dataset table; omit it for the edit-dataset modal so every available
 * leaf is returned with its current `isSelected` state.
 */
export const listConnectorTableAssets = async ({
  connectionId,
  catalogKey,
  isSelected,
}: {
  connectionId: string;
  catalogKey: string;
  isSelected?: boolean;
}): Promise<ConnectorTableRow[]> => {
  const response = await connectorConnectionAssetsApi.list(connectionId, {
    view: "tree",
    max_depth: resolveMaxDepth(catalogKey),
    ...(isSelected === undefined ? {} : { is_selected: isSelected }),
  });
  return flattenAssetTreeToTables(response.items);
};
